import { jwtDecode } from 'jwt-decode'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import Loading from 'quasar/src/plugins/loading/Loading.js';
import { route } from 'quasar/wrappers'
import { feathersClient } from 'src/feathers-client.js'
import { pinia } from 'src/modules/pinia.js'
import { checkPwaNavigation } from 'src/modules/pwa-update.js'
import routesCustomerPortal from 'src/router/routesCustomerPortal'
import { createMemoryHistory, createRouter, createWebHistory } from 'vue-router'

import routes from './routes'

const createHistory = process.env.SERVER ? createMemoryHistory : process.env.VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHistory

export const Router = createRouter({
  scrollBehavior: () => ({ left: 0, top: 0 }),
  routes: process.env.FRONTEND_TYPE === 'Admin' ? routes : routesCustomerPortal,
  history: createHistory(process.env.VUE_ROUTER_BASE),
})

const accessTokenStorageKey = process.env.FRONTEND_TYPE === 'Admin' ? 'vision-admin-jwt' : 'vision-customer-jwt'
const refreshTokenStorageKey = process.env.FRONTEND_TYPE === 'Admin' ? 'vision-admin-refresh-jwt' : 'vision-customer-refresh-jwt'

// Dedupe refresh calls (multiple guards can run quickly)
let refreshInFlight = null

function safeDecode(token) {
  if (!token) return null
  try {
    return jwtDecode(token)
  } catch (e) {
    return null
  }
}

function isExpired(decoded) {
  if (!decoded || !decoded.exp) return true
  return decoded.exp < DateTime.now().toSeconds()
}

async function refreshIfPossible(authStore) {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const refreshToken = window.localStorage.getItem(refreshTokenStorageKey)
    if (!refreshToken) return null

    const response = await feathersClient.service('authentication').create({
      strategy: 'refresh',
      refreshToken,
    })

    if (!response || !response.accessToken) return null

    await authStore.setAuthResponse(response)

    return response.accessToken
  })()

  try {
    return await refreshInFlight
  } finally {
    refreshInFlight = null
  }
}

export default route(async function (/* { store, ssrContext } */) {
  const authStore = useAuthStore(pinia)
  const { authResponse } = storeToRefs(authStore)

  Router.beforeEach(async (to, from) => {
    return checkPwaNavigation(to.fullPath, from.matched.length === 0)
  })

  Router.beforeEach(async (to, from, next) => {
    Loading.show()

    const rememberLoginRedirect = () => {
      if (to.meta?.requiresAuth === false) {
        return
      }

      if (String(to.path || '').startsWith('/auth')) {
        return
      }

      authStore.loginRedirect = to.fullPath || to.path || '/'
    }

    try {
      // If no authentication is needed for this page, continue
      if (to.meta?.requiresAuth === false) {
        return next()
      }

      await authStore.getPromise()

      let accessToken = authResponse.value?.accessToken
      if (!accessToken) {
        accessToken = window.localStorage.getItem(accessTokenStorageKey)
        if (accessToken) {
          await feathersClient.authentication.setAccessToken(accessToken)
        }
      }

      let decodedAccessToken = safeDecode(accessToken)

      if (!accessToken || isExpired(decodedAccessToken)) {
        const newAccessToken = await refreshIfPossible(authStore)

        if (!newAccessToken) {
          await authStore.fullLogout(false)
          rememberLoginRedirect()
          return next({ name: 'login' })
        }

        accessToken = newAccessToken
        decodedAccessToken = safeDecode(accessToken)

        if (!decodedAccessToken || isExpired(decodedAccessToken)) {
          await authStore.fullLogout(false)
          rememberLoginRedirect()
          return next({ name: 'login' })
        }
      }

      // MFA gate
      if (to.meta?.requiresMFA !== false && decodedAccessToken?.mfa !== true) {
        rememberLoginRedirect()
        let pendingPrimaryMethod = authStore.authResponse?.authentication?.payload?.pendingMFAPrimaryMethod
        let isMFAConfigured = authStore.authResponse?.user?.isMFAConfigured

        if (!pendingPrimaryMethod || typeof isMFAConfigured !== 'boolean') {
          const response = await authStore.reAuthenticateAndSync().catch(() => null)
          if (response) {
            pendingPrimaryMethod = response.pendingMFA?.primaryMethod || response.authentication?.payload?.pendingMFAPrimaryMethod || pendingPrimaryMethod
            isMFAConfigured = typeof response?.user?.isMFAConfigured === 'boolean' ? response.user.isMFAConfigured : isMFAConfigured
          }
        }

        const shouldActivateMFA = pendingPrimaryMethod === 'authenticator' && isMFAConfigured !== true
        const targetRouteName = shouldActivateMFA ? 'activateMfa' : 'verifyMfa'

        if (to.name === targetRouteName) {
          return next()
        }
        return next({ name: targetRouteName })
      }

      // SSO callback often lands on "/" directly; consume stored target once.
      const redirect = authStore.loginRedirect
      if (redirect && to.fullPath !== redirect && (to.name === 'main' || to.path === '/')) {
        authStore.clearLoginRedirect()
        return next(redirect)
      }

      // Check the resolved customer context before mounting a licensed page.
      if (process.env.FRONTEND_TYPE === 'Customer' && to.meta?.requiredLicense && !authStore.hasAssignedLicense(to.meta.requiredLicense)) {
        return next({ name: 'main', replace: true })
      }

      return next()
    } catch (e) {
      // Safe fallback
      try {
        await authStore.fullLogout(false)
        rememberLoginRedirect()
      } catch (_) {
        /* empty */
      }
      return next({ name: 'login' })
    } finally {
      Loading.hide()
    }
  })

  Router.afterEach(() => {
    Loading.hide()
  })

  return Router
})
